import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Download,
  FileText,
  Image as ImageIcon,
  Loader2,
  Search,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { CHANNEL_LABELS, CHANNEL_ORDER, ChannelChip } from "./channel-icon";
import {
  BROADCAST_PAGE_SIZES,
  BROADCAST_RETENTION_DAYS,
  CaptureFilter,
  CaptureItem,
  DEFAULT_BROADCAST_PAGE_SIZE,
  MAX_BROADCASTS_PER_WORKSPACE,
  broadcastsApi,
  useBroadcasts,
  useCaptures,
} from "./use-broadcasts";
import { downloadCaptures } from "./download-captures";
import { TablePagination } from "./table-pagination";

/** Delivery links are presigned for 7 days (engine DELIVERY_URL_TTL_SECONDS). */
const LINK_TTL_MS = 7 * 24 * 3600 * 1000;
/** Sentinel for a Select's "all" option (Radix can't hold an empty value). */
const ALL = "__all__";

/** "1 snapshot", "3 snapshots". */
function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function formatBytes(n: number): string {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function presetRange(preset: string): { from?: string; to?: string } {
  const now = Date.now();
  const day = 24 * 3600 * 1000;
  switch (preset) {
    case "today":
      return { from: new Date(new Date().setHours(0, 0, 0, 0)).toISOString() };
    case "week":
      return { from: new Date(now - 7 * day).toISOString() };
    case "month":
      return { from: new Date(now - 30 * day).toISOString() };
    default:
      return {};
  }
}

/**
 * Snapshots — a searchable, filterable, paged archive of every stored capture across the
 * workspace's broadcasts (the engine calls them captures; the UI says Snapshots, like OptiSigns).
 * Same server truth as the engine's captures endpoint; the tab holds no mirrored rows.
 *
 * Selection follows OptiSigns (scio3 #2149): it is keyed by runId and carries the row, so a
 * snapshot ticked on page 2 can still be downloaded or deleted from page 1; the header checkbox acts
 * on the page on screen only; and every filter or search change clears it, so Delete never reaches
 * a snapshot the user can no longer see. Download saves real files (see `download-captures`).
 */
export function CapturesTab(): JSX.Element {
  const [search, setSearch] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [broadcastId, setBroadcastId] = useState(ALL);
  const [channel, setChannel] = useState(ALL);
  const [selected, setSelected] = useState<Map<string, CaptureItem>>(new Map());
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_BROADCAST_PAGE_SIZE);

  // The broadcast filter lists every broadcast — one max page is the whole workspace.
  const { broadcasts } = useBroadcasts({}, { limit: MAX_BROADCASTS_PER_WORKSPACE, offset: 0 });

  const filter: CaptureFilter = useMemo(() => {
    const { from, to } = presetRange(datePreset);
    return {
      search: search.trim() || undefined,
      broadcastId: broadcastId === ALL ? undefined : broadcastId,
      channel: channel === ALL ? undefined : channel,
      from,
      to,
    };
  }, [search, datePreset, broadcastId, channel]);

  const { captures, total, fileCount, totalBytes, hasMore, loading, error, refresh } =
    useCaptures(filter, { limit: pageSize, offset: (page - 1) * pageSize });

  // The last snapshot of the last page was deleted: step back rather than show an empty page
  // whose footer still reads "4 / 3".
  useEffect(() => {
    if (!loading && !error && page > 1 && captures.length === 0) setPage(page - 1);
  }, [loading, error, page, captures.length]);

  // Every filter or search change: drop the selection (it belonged to the old list), back to page 1.
  const resetSelection = useCallback((): void => {
    setSelected(new Map());
    setPage(1);
  }, []);
  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T): void => {
      resetSelection();
      set(value);
    };

  // The header checkbox covers the page on screen and leaves rows ticked on other pages alone.
  const allSelected =
    captures.length > 0 && captures.every((c) => selected.has(c.runId));
  const selectedOnPage = captures.filter((c) => selected.has(c.runId)).length;

  function toggle(item: CaptureItem): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.runId)) next.delete(item.runId);
      else next.set(item.runId, item);
      return next;
    });
  }
  function toggleAll(): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) captures.forEach((c) => next.delete(c.runId));
      else captures.forEach((c) => next.set(c.runId, c));
      return next;
    });
  }

  async function handleDelete(runIds: string[]): Promise<void> {
    setDeleting(true);
    try {
      await Promise.all(runIds.map((id) => broadcastsApi.deleteCapture(id)));
      toast.success(`Deleted ${plural(runIds.length, "snapshot")}`);
      setSelected(new Map());
      setConfirmDelete(null);
      await refresh();
    } catch (err) {
      console.error("Failed to delete captures:", err);
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  }

  async function handleDownload(rows: CaptureItem[]): Promise<void> {
    if (rows.length === 0) return;
    setDownloading(true);
    try {
      const { downloaded, unavailable, failed } = await downloadCaptures(rows);
      if (downloaded > 0) toast.success(`Downloading ${plural(downloaded, "snapshot")}`);
      // Say what did not arrive — silence is what made the old bulk Download look like it worked.
      if (unavailable > 0) {
        toast.error(`${plural(unavailable, "snapshot")} expired and can no longer be downloaded`);
      }
      if (failed > 0) toast.error(`${plural(failed, "snapshot")} opened in a new tab instead`);
    } finally {
      setDownloading(false);
    }
  }

  // Every bulk action works on the rows the user ticked, whichever page they sit on.
  const selectedRows = [...selected.values()];

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="captures-search"
              placeholder="Search snapshots, broadcasts…"
              value={search}
              onChange={(e) => onFilter(setSearch)(e.target.value)}
              className="pl-9"
            />
          </div>
          <FilterSelect
            testid="captures-date"
            value={datePreset}
            onChange={onFilter(setDatePreset)}
            options={[
              { value: "all", label: "All time" },
              { value: "today", label: "Today" },
              { value: "week", label: "Last 7 days" },
              { value: "month", label: "Last 30 days" },
            ]}
          />
          <FilterSelect
            testid="captures-broadcast"
            value={broadcastId}
            onChange={onFilter(setBroadcastId)}
            options={[
              { value: ALL, label: "All broadcasts" },
              ...broadcasts.map((b) => ({ value: b.id, label: b.title })),
            ]}
          />
          <FilterSelect
            testid="captures-channel"
            value={channel}
            onChange={onFilter(setChannel)}
            options={[
              { value: ALL, label: "All channels" },
              ...CHANNEL_ORDER.map((c) => ({ value: c, label: CHANNEL_LABELS[c] ?? c })),
            ]}
          />
          {/* No status filter: the engine lists only captured runs (a snapshot is a stored file), so
              "failed" / "capturing" always came back empty. Failed runs live in Run history. */}
        </div>

        {/* Usage strip */}
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
          data-testid="captures-usage"
        >
          <span>
            <span className="font-medium text-foreground">{fileCount}</span> snapshots ·{" "}
            <span className="font-medium text-foreground">{formatBytes(totalBytes)}</span>
          </span>
          <span>
            Snapshots are kept {BROADCAST_RETENTION_DAYS} days · download links expire after
            7 days
          </span>
        </div>

        {/* Selection bar */}
        {selected.size > 0 && (
          <div
            className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
            data-testid="captures-selection-bar"
          >
            <span>{selected.size} selected</span>
            {selected.size > selectedOnPage && (
              <span className="text-xs text-muted-foreground">
                · {selected.size - selectedOnPage} on other pages
              </span>
            )}
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              disabled={downloading}
              onClick={() => void handleDownload(selectedRows)}
            >
              {downloading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-4 w-4" />
              )}
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setConfirmDelete(selectedRows.map((r) => r.runId))}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Delete
            </Button>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table className="min-w-[820px] table-fixed">
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all on this page"
                    data-testid="captures-select-all"
                  />
                </TableHead>
                <TableHead className="w-[44px]" />
                <TableHead className="w-[280px] text-[11px] font-semibold uppercase tracking-wide">
                  Snapshot
                </TableHead>
                <TableHead className="w-[170px] text-[11px] font-semibold uppercase tracking-wide">
                  Captured
                </TableHead>
                {/* No width — Send to absorbs the slack so File/Captured stay tight. */}
                <TableHead className="text-[11px] font-semibold uppercase tracking-wide">
                  Send to
                </TableHead>
                <TableHead className="w-[120px] text-[11px] font-semibold uppercase tracking-wide">
                  Link expiry
                </TableHead>
                <TableHead className="w-[70px] text-center text-[11px] font-semibold uppercase tracking-wide">
                  Download
                </TableHead>
                <TableHead className="w-[90px] text-right text-[11px] font-semibold uppercase tracking-wide">
                  Size
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && captures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : captures.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-10 text-center text-sm text-muted-foreground"
                    data-testid="captures-empty"
                  >
                    {fileCount > 0 ? "No snapshots match these filters." : "No snapshots yet."}
                  </TableCell>
                </TableRow>
              ) : (
                captures.map((item) => (
                  <CaptureRow
                    key={item.runId}
                    item={item}
                    selected={selected.has(item.runId)}
                    onToggle={() => toggle(item)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          totalCount={total}
          pageSize={pageSize}
          currentPage={page}
          hasNextPage={hasMore}
          loading={loading}
          pageSizes={BROADCAST_PAGE_SIZES}
          onPageSizeChange={setPageSize}
          onPageChange={setPage}
        />

        <AlertDialog
          open={confirmDelete !== null}
          onOpenChange={(open) => !open && setConfirmDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete snapshots?</AlertDialogTitle>
              <AlertDialogDescription>
                Delete {plural(confirmDelete?.length ?? 0, "snapshot")}? This can’t be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  if (confirmDelete) void handleDelete(confirmDelete);
                }}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}

function FilterSelect({
  testid,
  value,
  onChange,
  options,
}: {
  testid: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}): JSX.Element {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[130px]" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CaptureRow({
  item,
  selected,
  onToggle,
}: {
  item: CaptureItem;
  selected: boolean;
  onToggle: () => void;
}): JSX.Element {
  const Icon = item.kind === "pdf" ? FileText : ImageIcon;
  const expiresAt = new Date(item.capturedAt).getTime() + LINK_TTL_MS;
  const days = Math.ceil((expiresAt - Date.now()) / (24 * 3600 * 1000));

  return (
    <TableRow data-testid="capture-row" className={cn(selected && "bg-primary/5")}>
      <TableCell className="py-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label="Select"
          data-testid={`capture-select-${item.runId}`}
        />
      </TableCell>
      <TableCell className="py-3">
        <span className="flex h-7 w-9 items-center justify-center rounded border border-border bg-muted/50 text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
      </TableCell>
      <TableCell className="py-3">
        <div className="truncate font-medium text-foreground">
          {item.broadcastName || "Untitled"}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {item.kind === "pdf" ? "PDF" : "Image"}
        </div>
      </TableCell>
      <TableCell className="py-3">
        <div className="whitespace-nowrap text-sm tabular-nums">
          {formatDate(item.capturedAt)}
        </div>
        {item.manual && <div className="text-xs text-muted-foreground">Manual</div>}
      </TableCell>
      <TableCell className="py-3">
        <div className="flex flex-wrap gap-1.5">
          {[...item.channels]
            .sort(
              (a, b) =>
                CHANNEL_ORDER.indexOf(a as (typeof CHANNEL_ORDER)[number]) -
                CHANNEL_ORDER.indexOf(b as (typeof CHANNEL_ORDER)[number]),
            )
            .map((c) => (
              <ChannelChip key={c} type={c} tip={CHANNEL_LABELS[c] ?? c} />
            ))}
        </div>
      </TableCell>
      <TableCell className="py-3 text-xs">
        {item.expired || days <= 0 ? (
          <span className="text-muted-foreground">Expired</span>
        ) : (
          <span className={cn(days <= 1 && "text-amber-600 dark:text-amber-400")}>
            {days} day{days > 1 ? "s" : ""} left
          </span>
        )}
      </TableCell>
      <TableCell className="py-3 text-center">
        {item.artifactUrl ? (
          <button
            type="button"
            aria-label="Download"
            data-testid={`capture-download-${item.runId}`}
            className="text-muted-foreground hover:text-primary"
            onClick={() => void downloadCaptures([item])}
          >
            <Download className="mx-auto h-4 w-4" />
          </button>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="py-3 text-right tabular-nums">
        {!item.expired && item.bytes ? formatBytes(item.bytes) : "—"}
      </TableCell>
    </TableRow>
  );
}
